import { useEffect, useMemo, useState } from "react";
import { BarChart3, CheckCircle2, Loader2, RefreshCw, Save, Star } from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { PageHeader, Surface, UnifiedState } from "@/components/design/System";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const scaleLabels = { 5: "ممتاز", 4: "جيد جداً", 3: "جيد", 2: "مقبول", 1: "ضعيف" };
const roleLabel = { student: "طالب متدرب", association: "جمعية", mentor: "موجه", super_admin: "مدير النظام", donor: "مانح" };
const READ_ONLY_ROLES = new Set(["donor"]);
const BOARD_ROLES = new Set(["super_admin", "donor"]);

export default function PerformanceIndicators() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedApp, setSelectedApp] = useState("");
  const [selectedForm, setSelectedForm] = useState("");
  const [activeBoard, setActiveBoard] = useState("trainee_performance");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isReadOnly = READ_ONLY_ROLES.has(user?.role);
  const showBoards = BOARD_ROLES.has(user?.role);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [ctx, tpl] = await Promise.all([
        api.get("/monthly-evaluations/context"),
        api.get("/monthly-evaluations/templates"),
      ]);
      setData(ctx.data);
      setTemplates(tpl.data.templates || []);
      const firstApp = ctx.data.items?.[0]?.application_id || "";
      setSelectedApp((x) => x || firstApp);
      const firstDue = ctx.data.items?.[0]?.due_forms?.[0]?.form_key || "";
      setSelectedForm((x) => x || firstDue);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const app = data?.items?.find((x) => x.application_id === selectedApp);
  const template = templates.find((x) => x.key === selectedForm);
  const board = data?.dashboard?.boards?.find((x) => x.form_key === activeBoard) || data?.dashboard?.boards?.[0];

  return (
    <DashboardLayout>
      <PageHeader
        eyebrow={isReadOnly ? "لوحة المانح" : "التقييم الشهري"}
        title={showBoards ? "التقييمات الشهرية والمؤشرات" : "التقييم الشهري"}
        actions={
          <div className="flex items-center gap-2">
            {isReadOnly && (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-black text-amber-800" data-testid="donor-readonly-badge">
                سري · قراءة فقط
              </span>
            )}
            <button onClick={load} className="inline-flex min-h-11 items-center gap-2 rounded-xl border bg-white px-4 font-bold"><RefreshCw className="h-4 w-4" />تحديث</button>
          </div>
        }
      />
      {loading ? <UnifiedState type="loading" title="نحمّل التقييم الشهري" />
        : error ? <UnifiedState type="error" title="تعذر تحميل التقييم الشهري" description={error} onRetry={load} />
        : <div className="space-y-6">
          {!isReadOnly && <JourneySelector data={data} selectedApp={selectedApp} setSelectedApp={setSelectedApp} setSelectedForm={setSelectedForm} />}
          {showBoards && <DashboardBoards boards={data?.dashboard?.boards || []} active={activeBoard} onActive={setActiveBoard} board={board} />}
          {!isReadOnly && <MonthlyForm
            app={app}
            template={template}
            selectedForm={selectedForm}
            setSelectedForm={setSelectedForm}
            selectedApp={selectedApp}
            periodStart={data?.period_start}
            onSaved={load}
          />}
          <JourneyEvaluations
            journeys={data?.items || []}
            evaluations={data?.dashboard?.evaluations || []}
            expanded={isReadOnly}
          />
        </div>}
    </DashboardLayout>
  );
}


function JourneySelector({ data, selectedApp, setSelectedApp, setSelectedForm }) {
  return <Surface>
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-end">
      <label htmlFor="monthly-journey" className="block text-sm font-black">الرحلة
        <select
          id="monthly-journey"
          value={selectedApp}
          onChange={(e) => {
            const next = e.target.value;
            setSelectedApp(next);
            const nextApp = data.items?.find((x) => x.application_id === next);
            setSelectedForm(nextApp?.due_forms?.[0]?.form_key || "");
          }}
          className="mt-2 min-h-12 w-full rounded-xl border border-slate-200 bg-white px-3"
        >
          {(data?.items || []).map((x) => <option key={x.application_id} value={x.application_id}>{x.student_name || "متدرب"} · {x.association_name || "جمعية"}</option>)}
        </select>
      </label>
      <div className="rounded-xl bg-teal-50 p-3 text-sm font-bold leading-7 text-teal-900">شهر التقييم الحالي: {data?.period_label}</div>
    </div>
  </Surface>;
}

function DashboardBoards({ boards, active, onActive, board }) {
  if (!boards.length) return null;
  return <Surface>
    <div className="flex flex-wrap items-center gap-2" data-testid="donor-boards-tabs">
      {boards.map((b) => <button key={b.form_key} onClick={() => onActive(b.form_key)} className={`min-h-11 rounded-xl px-4 text-sm font-black ${active === b.form_key ? "bg-teal-700 text-white" : "border border-slate-200 bg-white text-slate-700"}`} data-testid={`board-tab-${b.form_key}`}>{b.title}</button>)}
    </div>
    {board && <div className="mt-6 space-y-5">
      <div className="flex items-center gap-3"><BarChart3 className="h-6 w-6 text-teal-700" /><div><h2 className="text-xl font-black">{board.title}</h2><p className="text-sm text-slate-500">المستهدفات نهائية للدورة أو الفرصة، والشهر يضيف نتيجة جديدة ولا يعيد المستهدف للصفر.</p></div></div>
      <div className="grid gap-3 md:grid-cols-2">
        {board.indicators.map((kpi) => <KpiChart key={kpi.key} item={kpi} />)}
      </div>
      <div className="rounded-2xl bg-slate-50 p-4">
        <h3 className="font-black">بنود النموذج تحت محاورها الأصلية</h3>
        <div className="mt-3 grid gap-3 lg:grid-cols-3">
          {board.axes.map((axis) => <div key={axis.key} className="rounded-xl bg-white p-3"><b>{axis.title}</b><ul className="mt-2 space-y-1 text-sm text-slate-600">{axis.items.map((it) => <li key={it.key}>• {it.label}</li>)}</ul></div>)}
        </div>
      </div>
    </div>}
  </Surface>;
}

const TREND_META = {
  "صاعد": { cls: "bg-emerald-50 text-emerald-800 border-emerald-100" },
  "هابط": { cls: "bg-rose-50 text-rose-700 border-rose-100" },
  "مستقر": { cls: "bg-slate-50 text-slate-700 border-slate-200" },
  "لا توجد مقارنة سابقة": { cls: "bg-slate-50 text-slate-500 border-slate-200" },
};

function chartTypeStyle(type) {
  if (type === "percent") return { fill: "bg-teal-600", track: "bg-teal-50", suffix: "%", scale: "نسبة مئوية" };
  if (type === "rating")  return { fill: "bg-amber-500", track: "bg-amber-50", suffix: "/5", scale: "مقياس 1–5" };
  if (type === "count")   return { fill: "bg-indigo-600", track: "bg-indigo-50", suffix: "", scale: "عدد" };
  return { fill: "bg-slate-600", track: "bg-slate-50", suffix: "", scale: "نعم/لا" };
}

function KpiChart({ item }) {
  const type = item.target_type || "count";
  const style = chartTypeStyle(type);
  const target = item.target_numeric;
  const achieved = item.achieved_numeric;
  const hasData = achieved !== null && achieved !== undefined && (type !== "bool");
  const targetPct = type === "percent" ? Math.min(100, target || 0) : type === "rating" ? ((target || 5) / 5) * 100 : 100;
  const achievedPct = !hasData ? 0
    : type === "percent" ? Math.min(120, achieved)
    : type === "rating" ? Math.min(120, (achieved / 5) * 100)
    : target && target > 0 ? Math.min(120, (achieved / target) * 100)
    : 100;
  const trend = TREND_META[item.trend] || TREND_META["مستقر"];
  // Monthly series in the backend averages `average_score` (1-5), so the
  // "current month" number is only meaningful for rating indicators. Percent
  // and count indicators would show a rating that doesn't match their scale.
  const showCurrentMonth = type === "rating" && item.current_month != null;
  const remaining = hasData && type !== "bool" && target != null
    ? type === "percent" ? Math.max(0, target - achieved)
      : type === "rating" ? Math.round(Math.max(0, target - achieved) * 10) / 10
      : Math.max(0, target - achieved)
    : 0;
  const remainingUnit = type === "percent" ? "% متبقية" : type === "rating" ? " نقطة متبقية" : " متبقية";
  const targetDisplay = String(item.final_target).replace("–", "-");

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm" data-testid={`kpi-chart-${item.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs font-black text-teal-700">{item.axis}</div>
          <h3 className="mt-1 text-sm font-black leading-6 text-slate-900">{item.indicator}</h3>
        </div>
        <span className="shrink-0 text-[10px] font-bold uppercase text-slate-400 whitespace-nowrap">{style.scale}</span>
      </div>

      {type === "bool" ? (
        <div className="mt-4 flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-black ${item.achieved_so_far === "نعم" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
            <span>{item.achieved_so_far === "نعم" ? "✓" : "○"}</span>
            <span>{item.achieved_so_far || "لا"}</span>
          </span>
          <span className="text-xs text-slate-500">المستهدف: <span dir="ltr">{targetDisplay}</span></span>
        </div>
      ) : !hasData ? (
        <div className="mt-4 rounded-lg bg-slate-50 border border-dashed border-slate-200 p-3 text-xs text-slate-500">
          لا توجد بيانات مسجّلة بعد لهذا المؤشر — سيتم عرضه فور تسجيل أول تقييم.
        </div>
      ) : (
        <>
          <div className="mt-4">
            <div className={`relative h-4 w-full rounded-full ${style.track} overflow-hidden`} dir="ltr">
              <div
                className={`h-full ${style.fill} transition-all`}
                style={{ width: `${Math.min(100, achievedPct)}%` }}
                aria-label="المتحقق"
              />
              {/* Target marker */}
              <div
                className="absolute top-0 h-full w-0.5 bg-slate-900"
                style={{ insetInlineStart: `${Math.min(100, targetPct)}%` }}
                aria-label="خط المستهدف"
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500 arabic-numerals" dir="ltr">
              <span>{type === "rating" ? "0/5" : "0%"}</span>
              <span>{type === "rating" ? "5/5" : type === "percent" ? "100%" : "100"}</span>
            </div>
          </div>

          <div className={`mt-3 grid gap-2 text-xs ${showCurrentMonth ? "grid-cols-3" : "grid-cols-2"}`}>
            <div className="rounded-xl bg-slate-50 p-2 text-center">
              <div className="text-[10px] text-slate-500">المستهدف</div>
              <div className="mt-0.5 font-black arabic-numerals text-slate-900" dir="ltr">{targetDisplay}</div>
            </div>
            <div className={`rounded-xl p-2 text-center ${style.track}`}>
              <div className="text-[10px] text-slate-500">المتحقق</div>
              <div className="mt-0.5 font-black arabic-numerals text-slate-900" dir="ltr">
                {achieved}{style.suffix}
              </div>
            </div>
            {showCurrentMonth && (
              <div className="rounded-xl bg-slate-50 p-2 text-center">
                <div className="text-[10px] text-slate-500">الشهر الحالي</div>
                <div className="mt-0.5 font-black arabic-numerals text-slate-900" dir="ltr">
                  {item.current_month}/5
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <div className="mt-3 flex items-center justify-between gap-2">
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-black ${trend.cls}`}>
          اتجاه: {item.trend}
        </span>
        {hasData && target != null && type !== "bool" && (
          <span className={`text-[10px] font-bold ${achieved >= target ? "text-emerald-700" : "text-amber-700"}`}>
            {achieved >= target ? "تحقق المستهدف" : <><span className="arabic-numerals" dir="ltr">{remaining}</span>{remainingUnit}</>}
          </span>
        )}
      </div>
    </div>
  );
}

function MonthlyForm({ app, template, selectedForm, setSelectedForm, selectedApp, periodStart, onSaved }) {
  const dueForms = app?.due_forms || [];
  const [ratings, setRatings] = useState({});
  const [extra, setExtra] = useState({ monthly_highlights: "", notes: "", attendance_present_days: "", attendance_absent_days: "", recommendation: "", challenge_priority: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const next = {};
    for (const axis of template?.axes || []) for (const it of axis.items || []) next[it.key] = 5;
    setRatings(next);
    setExtra({ monthly_highlights: "", notes: "", attendance_present_days: "", attendance_absent_days: "", recommendation: "", challenge_priority: "" });
  }, [template?.key, template?.axes, selectedApp]);

  if (!app) return <Surface><UnifiedState title="لا توجد رحلة نشطة" description="تظهر نماذج التقييم الشهري عند وجود رحلة مرتبطة بدورك." /></Surface>;
  if (!dueForms.length) return <Surface><UnifiedState title="لا يوجد نموذج مطلوب من دورك" description="يمكنك الاطلاع على المؤشرات والتقييمات السابقة فقط." /></Surface>;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        application_id: selectedApp,
        form_key: selectedForm,
        period_start: periodStart,
        ratings,
        monthly_highlights: extra.monthly_highlights,
        notes: extra.notes,
        recommendation: extra.recommendation,
        challenge_priority: extra.challenge_priority || null,
        attendance_present_days: extra.attendance_present_days === "" ? null : Number(extra.attendance_present_days),
        attendance_absent_days: extra.attendance_absent_days === "" ? null : Number(extra.attendance_absent_days),
      };
      await api.post("/monthly-evaluations/responses", payload);
      toast.success("تم حفظ التقييم الشهري وربطه بالرحلة");
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return <Surface>
    <div className="flex items-start gap-3"><div className="grid h-11 w-11 place-items-center rounded-xl bg-amber-50 text-amber-700"><Star /></div><div><h2 className="text-xl font-black">التقييم الشهري</h2><p className="mt-1 text-sm text-slate-500">كل مقيم يحفظ تقييمًا واحدًا لنفس الرحلة ونفس الشهر ونفس النموذج.</p></div></div>
    <form onSubmit={submit} className="mt-6 space-y-5">
      <div className="grid gap-3 md:grid-cols-2">
        <label className="block text-sm font-bold">النموذج
          <select value={selectedForm} onChange={(e) => setSelectedForm(e.target.value)} className="mt-2 min-h-12 w-full rounded-xl border border-slate-200 bg-white px-3">
            {dueForms.map((f) => <option key={f.form_key} value={f.form_key}>{f.title}</option>)}
          </select>
        </label>
        <div className="rounded-xl bg-slate-50 p-3 text-sm leading-7">
          <b>{app.student_name || "المتدرب"}</b><br />{app.association_name || "الجمعية"} · {app.mentor_name || "بدون موجه مرتبط"}
        </div>
      </div>
      {(template?.axes || []).map((axis) => <div key={axis.key} className="rounded-2xl border border-slate-100 p-4"><h3 className="font-black">{axis.title}</h3><div className="mt-4 grid gap-3 md:grid-cols-2">{axis.items.map((it) => <RatingItem key={it.key} item={it} value={ratings[it.key] || 5} onChange={(v) => setRatings((x) => ({ ...x, [it.key]: v }))} />)}</div></div>)}
      <div className="grid gap-3 md:grid-cols-2">
        <label className="block text-sm font-bold">أبرز إنجازات المتدرب خلال الشهر / أبرز المبادرات<textarea value={extra.monthly_highlights} onChange={(e) => setExtra((x) => ({ ...x, monthly_highlights: e.target.value }))} className="mt-2 min-h-28 w-full rounded-xl border border-slate-200 p-3" /></label>
        <label className="block text-sm font-bold">ملاحظات عامة<textarea value={extra.notes} onChange={(e) => setExtra((x) => ({ ...x, notes: e.target.value }))} className="mt-2 min-h-28 w-full rounded-xl border border-slate-200 p-3" /></label>
        <label className="block text-sm font-bold">عدد أيام الحضور في الشهر<input type="number" min="0" max="31" value={extra.attendance_present_days} onChange={(e) => setExtra((x) => ({ ...x, attendance_present_days: e.target.value }))} className="mt-2 min-h-12 w-full rounded-xl border border-slate-200 px-3" /></label>
        <label className="block text-sm font-bold">عدد أيام الغياب في الشهر<input type="number" min="0" max="31" value={extra.attendance_absent_days} onChange={(e) => setExtra((x) => ({ ...x, attendance_absent_days: e.target.value }))} className="mt-2 min-h-12 w-full rounded-xl border border-slate-200 px-3" /></label>
      </div>
      <label className="block text-sm font-bold">التوصية<textarea value={extra.recommendation} onChange={(e) => setExtra((x) => ({ ...x, recommendation: e.target.value }))} className="mt-2 min-h-24 w-full rounded-xl border border-slate-200 p-3" /></label>
      <div>
        <div className="text-sm font-bold mb-2">أولوية التحدي في الملاحظات العامة</div>
        <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="أولوية التحدي" data-testid="challenge-priority-group">
          {[{k:"high",label:"عالية",cls:"border-rose-300 bg-rose-50 text-rose-800"},{k:"medium",label:"متوسطة",cls:"border-amber-300 bg-amber-50 text-amber-800"},{k:"low",label:"منخفضة",cls:"border-emerald-300 bg-emerald-50 text-emerald-800"}].map((opt) => (
            <button
              key={opt.k}
              type="button"
              role="radio"
              aria-checked={extra.challenge_priority === opt.k}
              onClick={() => setExtra((x) => ({ ...x, challenge_priority: x.challenge_priority === opt.k ? "" : opt.k }))}
              className={`min-h-11 rounded-xl border-2 text-sm font-black transition-all ${
                extra.challenge_priority === opt.k
                  ? `${opt.cls} ring-2 ring-offset-2 ring-current`
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
              data-testid={`challenge-priority-${opt.k}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="mt-1 text-xs text-slate-500">اختياري — يساعد الإدارة على ترتيب التدخّلات.</div>
      </div>
      <button disabled={busy} className="inline-flex min-h-12 items-center gap-2 rounded-xl bg-teal-700 px-5 font-bold text-white disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}حفظ التقييم الشهري</button>
    </form>
  </Surface>;
}

function RatingItem({ item, value, onChange }) {
  return <div className="rounded-xl bg-slate-50 p-3"><div className="font-bold leading-7">{item.label}</div><div className="mt-3 grid grid-cols-5 gap-2">{[1, 2, 3, 4, 5].map((n) => <button type="button" key={n} onClick={() => onChange(n)} className={`min-h-11 rounded-xl border text-sm font-black ${value === n ? "border-teal-700 bg-teal-50 text-teal-800" : "border-slate-200 bg-white"}`}>{n}</button>)}</div><div className="mt-2 text-xs text-slate-500">{scaleLabels[value]}</div></div>;
}

function JourneyEvaluations({ journeys, evaluations, expanded = false }) {
  const [query, setQuery] = useState("");
  const [openJourneyId, setOpenJourneyId] = useState(null);

  // Group evaluations by application_id + enrich with journey context from `journeys`.
  const groups = useMemo(() => {
    const byId = new Map(journeys.map((j) => [j.application_id, j]));
    const map = new Map();
    for (const e of evaluations) {
      const arr = map.get(e.application_id) || [];
      arr.push(e);
      map.set(e.application_id, arr);
    }
    return Array.from(map.entries()).map(([appId, evs]) => {
      const j = byId.get(appId) || {};
      const avg = evs.length ? evs.reduce((s, e) => s + (Number(e.average_score) || 0), 0) / evs.length : 0;
      return {
        application_id: appId,
        student_name: j.student_name || "طالب غير معروف",
        student_university: j.student_university,
        association_name: j.association_name || "بدون جمعية",
        mentor_name: j.mentor_name || "بدون موجّه",
        opportunity_title: j.opportunity_title || "فرصة",
        training_field: j.training_field,
        evaluations: evs.slice().sort((a, b) => (b.period_start || "").localeCompare(a.period_start || "")),
        average: Math.round(avg * 100) / 100,
        forms_count: new Set(evs.map((e) => e.form_key)).size,
      };
    }).sort((a, b) => (a.student_name || "").localeCompare(b.student_name || "", "ar"));
  }, [journeys, evaluations]);

  // Auto-open the first journey on desktop for donors so the value is instantly visible.
  useEffect(() => {
    if (expanded && !openJourneyId && groups[0]) setOpenJourneyId(groups[0].application_id);
  }, [expanded, groups, openJourneyId]);

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return groups;
    return groups.filter((g) =>
      [g.student_name, g.association_name, g.mentor_name, g.opportunity_title]
        .filter(Boolean).some((t) => String(t).includes(q))
    );
  }, [groups, query]);

  if (!evaluations.length) {
    return <Surface><UnifiedState title="لا توجد تقييمات شهرية محفوظة بعد" description="بعد حفظ أول تقييم شهري سيظهر هنا مع الفترة والمقيم والبنود." /></Surface>;
  }

  return (
    <Surface>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          <h2 className="text-xl font-black">التقييمات مجمّعة حسب الرحلة</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black arabic-numerals">{groups.length} رحلة · {evaluations.length} تقييم</span>
        </div>
      </div>
      {expanded && (
        <p className="mt-2 text-xs leading-6 text-slate-500">
          كل بطاقة تمثّل رحلة تدريب مع اسم الطالب والجمعية والموجّه والفرصة. اضغط الرحلة لعرض جميع التقييمات المسجّلة عليها من الأدوار الثلاثة، كل تقييم بمحاوره وبنوده الأصلية.
        </p>
      )}
      <div className="mt-4">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="ابحث بالطالب أو الجمعية أو الموجّه أو الفرصة…"
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-teal-500 focus:outline-none"
          data-testid="journey-eval-search"
        />
      </div>

      <div className="mt-5 space-y-3">
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">لا نتائج مطابقة لبحثك.</div>
        ) : filtered.map((g) => (
          <JourneyCard
            key={g.application_id}
            group={g}
            open={openJourneyId === g.application_id}
            onToggle={() => setOpenJourneyId((cur) => cur === g.application_id ? null : g.application_id)}
          />
        ))}
      </div>
    </Surface>
  );
}

function JourneyCard({ group, open, onToggle }) {
  return (
    <article className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm" data-testid={`journey-card-${group.application_id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-start p-4 hover:bg-slate-50 active:bg-slate-100 transition-colors"
        aria-expanded={open}
        data-testid={`journey-toggle-${group.application_id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-slate-900">
              <span className="text-lg font-black">{group.student_name}</span>
              {group.student_university && (
                <span className="text-[11px] text-slate-500">{group.student_university}</span>
              )}
            </div>
            <div className="grid gap-1.5 text-xs md:grid-cols-3">
              <span className="inline-flex items-center gap-1.5 rounded-lg bg-teal-50 px-2 py-1 text-teal-800">
                <span className="text-[10px] font-black uppercase">الفرصة</span>
                <span className="truncate font-semibold">{group.opportunity_title}</span>
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-50 px-2 py-1 text-indigo-800">
                <span className="text-[10px] font-black uppercase">الجمعية</span>
                <span className="truncate font-semibold">{group.association_name}</span>
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-2 py-1 text-amber-800">
                <span className="text-[10px] font-black uppercase">الموجّه</span>
                <span className="truncate font-semibold">{group.mentor_name}</span>
              </span>
            </div>
          </div>
          <div className="shrink-0 flex flex-col items-end gap-1.5">
            <span className="rounded-xl bg-teal-50 px-3 py-1.5 text-sm font-black text-teal-800 arabic-numerals" dir="ltr">
              {group.average}/5
            </span>
            <span className="text-[10px] font-bold text-slate-500 arabic-numerals">{group.evaluations.length} تقييم</span>
            <span className={`text-[10px] font-bold text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 bg-slate-50/60 p-4 space-y-3" data-testid={`journey-content-${group.application_id}`}>
          {group.evaluations.map((e) => <EvaluationTile key={e.id} evaluation={e} />)}
        </div>
      )}
    </article>
  );
}

function EvaluationTile({ evaluation: e }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl bg-white border border-slate-100 overflow-hidden" data-testid={`eval-tile-${e.id}`}>
      <button type="button" onClick={() => setOpen((x) => !x)} className="w-full text-start p-3 hover:bg-slate-50 active:bg-slate-100">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-black arabic-numerals">{e.period_label}</span>
              <b className="text-sm">{e.form_title}</b>
            </div>
            <div className="mt-1 text-[11px] text-slate-500">
              المقيّم: <b className="text-slate-700">{e.evaluator_name || "—"}</b>
              <span className="mx-1">·</span>
              دور: {roleLabel[e.evaluator_role] || e.evaluator_role}
            </div>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            <span className="rounded-lg bg-teal-50 px-2.5 py-1 text-xs font-black text-teal-800 arabic-numerals" dir="ltr">
              {e.average_score}/5
            </span>
            <span className={`text-[10px] font-bold text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 p-3 space-y-3">
          {(e.attendance_present_days != null || e.attendance_absent_days != null) && (
            <div className="grid grid-cols-2 gap-2 text-xs">
              <span className="rounded-xl bg-emerald-50 p-2 text-emerald-800 arabic-numerals">أيام حضور: <b>{e.attendance_present_days ?? "—"}</b></span>
              <span className="rounded-xl bg-rose-50 p-2 text-rose-800 arabic-numerals">أيام غياب: <b>{e.attendance_absent_days ?? "—"}</b></span>
            </div>
          )}
          {e.monthly_highlights && (
            <div className="rounded-xl bg-slate-50 p-3 text-sm leading-7">
              <div className="text-[11px] font-black uppercase text-slate-500">أبرز إنجازات/مبادرات الشهر</div>
              <div className="mt-1 text-slate-700">{e.monthly_highlights}</div>
            </div>
          )}
          {e.notes && (
            <div className="rounded-xl bg-slate-50 p-3 text-sm leading-7">
              <div className="text-[11px] font-black uppercase text-slate-500">ملاحظات عامة</div>
              <div className="mt-1 text-slate-700">{e.notes}</div>
            </div>
          )}
          {e.recommendation && (
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-sm leading-7">
              <div className="text-[11px] font-black uppercase text-amber-800">التوصية</div>
              <div className="mt-1 text-amber-900">{e.recommendation}</div>
            </div>
          )}
          {e.challenge_priority && (
            <div className="rounded-xl border p-3 text-sm leading-7" style={{
              borderColor: e.challenge_priority === "high" ? "#fecaca" : e.challenge_priority === "medium" ? "#fde68a" : "#a7f3d0",
              backgroundColor: e.challenge_priority === "high" ? "#fef2f2" : e.challenge_priority === "medium" ? "#fffbeb" : "#ecfdf5",
            }} data-testid={`challenge-priority-badge-${e.id}`}>
              <div className="text-[11px] font-black uppercase text-slate-600">أولوية التحدي</div>
              <div className="mt-1 font-black">
                {e.challenge_priority === "high" ? "عالية" : e.challenge_priority === "medium" ? "متوسطة" : "منخفضة"}
              </div>
            </div>
          )}
          {Array.isArray(e.axes) && e.axes.length > 0 && (
            <div className="rounded-xl bg-white ring-1 ring-slate-100 p-3">
              <div className="text-xs font-black uppercase text-slate-500 mb-2">بنود التقييم</div>
              <div className="grid gap-3 md:grid-cols-2">
                {e.axes.map((axis) => (
                  <div key={axis.key} className="rounded-lg bg-slate-50 p-3">
                    <b className="text-sm">{axis.title}</b>
                    <ul className="mt-2 space-y-1 text-xs">
                      {axis.items.map((it) => {
                        const val = (e.ratings || {})[it.key];
                        return (
                          <li key={it.key} className="flex items-center justify-between gap-2">
                            <span className="text-slate-600">{it.label}</span>
                            <span className="shrink-0 rounded-md bg-white ring-1 ring-slate-200 px-2 py-0.5 font-black arabic-numerals" dir="ltr">
                              {val ?? "—"}/5
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
